import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import { Plus, Trash2 } from "lucide-react";
import Select from "react-select";

import { familyMemberFields } from "./ProfileData";
// import { validationSchema } from "../validation";
import {
  getFamilyDetails,
  patchFamilyDetails,
} from "../../../ApiHelper/url_Helpers";
import { RiseLoader } from "react-spinners";
import * as Yup from "yup";

const getAge = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};



/* ---------- FORM FIELD COMPONENT ---------- */
const FormField = ({ field, index, formik, readOnly }) => {
  const name = `family_members[${index}].${field.id}`;
  const isError =
    formik.touched.family_members?.[index]?.[field.id] &&
    Boolean(formik.errors.family_members?.[index]?.[field.id]);

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.5rem",
      fontSize: "14px",
      backgroundColor: readOnly ? "#F3F4F6" : "#FAFBFD",
      borderColor: isError
        ? "#ef4444"
        : state.isFocused
          ? "#00B8D9"
          : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #00B8D9" : "none",
      cursor: readOnly ? "not-allowed" : "default",
      "&:hover": { borderColor: readOnly ? "#e5e7eb" : "#00B8D9" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="flex flex-col p-2 md:p-4 gap-1.5 w-full">
      <label className="text-[13px] md:text-sm font-semibold text-gray-700">
        {field.label}
        {field.required && !readOnly && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>

      {field.type === "select" ? (
        <Select
          name={name}
          options={field.options?.map((opt) => ({ value: opt, label: opt }))}
          value={
            field.options
              ?.map((opt) => ({ value: opt, label: opt }))
              .find(
                (o) =>
                  o.value === formik.values.family_members[index][field.id],
              ) || null
          }
          isDisabled={readOnly}
          onChange={(option) =>
            formik.setFieldValue(name, option ? option.value : "")
          }
          onBlur={() => formik.setFieldTouched(name, true)}
          styles={customStyles}
          placeholder="Select"
          isClearable={!readOnly}
          menuPortalTarget={document.body}
        />
      ) : (
        <input
          type={field.type}
          name={name}
          value={formik.values.family_members[index][field.id] || ""}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          readOnly={readOnly}
          placeholder={`Enter ${field.label}`}
          className={`border rounded-lg px-3 py-2 text-sm outline-none transition-all ${
            readOnly
              ? "bg-gray-100 cursor-not-allowed border-gray-200"
              : "bg-[#FAFBFD] border-gray-300 focus:ring-1 focus:ring-[#00B8D9]"
          } ${isError ? "border-red-500 ring-1 ring-red-100" : ""}`}
        />
      )}

      {isError && !readOnly && (
        <p className="text-red-500 text-[10px] md:text-xs font-medium mt-1">
          {formik.errors.family_members[index][field.id]}
        </p>
      )}
    </div>
  );
};

const emptyMember = {
  name: "",
  relationship: "",
  date_of_birth: "",
  gender: "",
  blood_group: "",
  nationality: "",
  is_minor: false,
  has_mental_illness: false,
};

/* ---------- MAIN COMPONENT ---------- */
const FamilyDetails = forwardRef(({ onEditStatusChange, onDirtyChange }, ref)  => {
  const [loading, setLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(false);
    const initialValues = useMemo(
    () => ({ family_members: [] }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        family_members: Yup.array().of(
          Yup.object().shape({
            relationship: Yup.string().required("Relationship is required"),

            date_of_birth: Yup.date()
              .required("DOB is required")
              .test(
                "age-validation",
                "Age must be 18+ for this relationship",
                function (value) {
                  const { relationship } = this.parent;

                  if (!value) return false;

                  const allowedRelations = ["brother", "sister", "son"];

                  // If relation is Brother/Sister/Son → allow any age
                  if (allowedRelations.includes(relationship)) {
                    return true;
                  }

                  // Others → must be 18+
                  return getAge(value) >= 18;
                }
              ),
          })
        ),
      }),
    [],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      await patchFamilyDetails(values);
    },
  });

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyChange]);


  const hasFetchedRef = useRef(false);
  const formikRef = useRef(null);

  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFamilyDetails();
      const canEdit = response?.family_members?.action?.isEdit ?? false;
      setIsEditable(canEdit);
      onEditStatusChange(canEdit);

      const rawMembers = response?.family_members?.data || response?.data || [];
      const members = Array.isArray(rawMembers)
        ? rawMembers
        : rawMembers
        ? [rawMembers]
        : [];
        
      formikRef.current?.resetForm({
        values: {
          family_members: members.length > 0 ? members : [emptyMember],
        },
      });
    } catch (error) {
      console.error("Error fetching family details", error);
      if (error?.status === 404) {
        setIsEditable(true);
        formikRef.current?.resetForm({
          values: {
            family_members: [emptyMember],
          },
        });
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [onEditStatusChange]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [fetchData]);

  useImperativeHandle(ref, () => ({
    handleChildSave: async () => {
      if (!isEditable) return { success: false, error: "Editing is locked" };

      const errors = await formik.validateForm();
      formik.setTouched({
        family_members: formik.values.family_members.map(() =>
          familyMemberFields.reduce((acc, f) => ({ ...acc, [f.id]: true }), {}),
        ),
      });

      if (Object.keys(errors).length > 0)
        return { success: false, error: "Validation failed" };

      try {
        await formik.submitForm();
        setIsEditable(false);
        await fetchData();
        return { success: true };
      } catch (error) {
        return { success: false, error: error?.message || "Failed to save" };
      }
    },
  }));

  return (
    <FormikProvider value={formik}>
      {loading ? (
        <div
          className={`flex items-center justify-center w-full h-full min-h-[350px] bg-white`}
        >
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        <div>
          <form className="w-full pb-10">
            <FieldArray name="family_members">
              {({ push, remove }) => (
                <>
                  {formik.values.family_members.map((member, i) => (
                    <div
                      key={i}
                      className="border-2 border-black/5 bg-white rounded-xl mb-8 shadow-sm overflow-hidden"
                    >
                      <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50/50">
                        <h3 className="font-bold text-gray-800 text-sm md:text-base tracking-tight">
                          Family Member {i + 1}
                        </h3>
                        <div className="flex items-center gap-3">
                          {!isEditable && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded font-bold uppercase">
                              Locked
                            </span>
                          )}
                          {isEditable &&
                            formik.values.family_members.length > 1 && (
                              <button
                                type="button"
                                onClick={() => remove(i)}
                                className="text-red-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 p-2">
                        {familyMemberFields.map((field) => (
                          <FormField
                            key={field.id}
                            field={field}
                            index={i}
                            formik={formik}
                            readOnly={!isEditable}
                          />
                        ))}
                      </div>

                      {/* Checkboxes Section */}
                      <div className="flex flex-wrap gap-8 px-6 py-4 bg-gray-50/30 border-t border-dashed border-gray-100">
                        <label
                          className={`flex items-center gap-3 text-sm font-medium select-none ${!isEditable ? "cursor-not-allowed text-gray-400" : "cursor-pointer"}`}
                        >
                          <input
                            type="checkbox"
                            disabled={!isEditable}
                            checked={member.is_minor || false}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `family_members[${i}].is_minor`,
                                e.target.checked,
                              )
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#00B8D9] focus:ring-[#00B8D9]"
                          />
                          Is Minor?
                        </label>

                        <label
                          className={`flex items-center gap-3 text-sm font-medium select-none ${!isEditable ? "cursor-not-allowed text-gray-400" : "cursor-pointer"}`}
                        >
                          <input
                            type="checkbox"
                            disabled={!isEditable}
                            checked={member.has_mental_illness || false}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `family_members[${i}].has_mental_illness`,
                                e.target.checked,
                              )
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#00B8D9] focus:ring-[#00B8D9]"
                          />
                          Mental Illness?
                        </label>
                      </div>
                    </div>
                  ))}

                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => push(emptyMember)}
                      className="w-full flex justify-center items-center gap-2 font-bold text-[#00B8D9] py-4 border-2 border-dashed border-cyan-100 rounded-xl hover:bg-cyan-50 transition-all mb-10"
                    >
                      <Plus size={18} /> Add Another Family Member
                    </button>
                  )}
                </>
              )}
            </FieldArray>
          </form>
        </div>
      )}
    </FormikProvider>
  );
});

export default FamilyDetails;
